import { FC, ReactNode, SyntheticEvent, useState } from "react";
import { Tabs, Tab, Box, Paper, Typography, CircularProgress, useMediaQuery } from "@mui/material";
import InfoTab from "./components/Tabs/InfoTab/InfoTab";
import RegistrationTab from "./components/Tabs/RegistrationTab/RegistrationTab";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Summary from "./components/Summary/Summary";
import { useEffect } from 'react';
import { useAppDispatch } from 'shared/config/configureStore';
import { useTranslation } from 'react-i18next';
import { getMarathonsById } from "api/thunk/marathons/getActions/getMarathonsById";
import { getProductsByMarathonId } from "api/thunk/products/getActions/getProductsByMarathonId";
import { useAppSelector } from "shared/lib/hooks/hooks";
import dayjs from "dayjs";
import { getMarathonsHasUserInsurance } from "api/thunk/marathons/getActions/getMarathonsHasUserInsurance";
import { useNavigate, useParams } from "react-router-dom";
import MobileSummery from "./components/MobileSummery/MobileSummery";
import { useTheme, Theme } from '@mui/material/styles';
import { createSignalRConnection } from "shared/lib/signalr";
import { HubConnection } from "@microsoft/signalr";
import { changeDistanceOfMarathon } from "entities/admin/model/thunk/changeDistance";
import { registerUser } from "api/thunk/applications/postActions/registerUser";
import SecondConfirmModal from "shared/ui/SecondConfirmModal/SecondConfirmModal";
import { getMarathonAgreementsByMarathonId } from "api/thunk/offerAgreements/getActions/getMarathonAgreementById";
import { getMarathonUserRegistered } from "api/thunk/marathons/getActions/getMarathonUserRegistered";
import { getUserMarathonProducts } from "api/thunk/products/getActions/getUserMarathonProducts";

interface TabItem {
  label: string;
  component: ReactNode;
}

export interface IProducts {
  productId: number;
  quantity: number;
  price: number;
  name: string;
}

const RegistrationPage: FC = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const theme = useTheme<Theme>();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id: marathonId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [changeDistance, setChangeDistance] = useState(false);

  const [enteredPromocode, setEnteredPromocode] = useState<string>("");

  const [paymentConnection, setPaymentConnection] = useState<HubConnection | null>(null);
  const [isChangeDistanceModalOpen, setIsChangeDistanceModalOpen] = useState(false);

  const marathon = useAppSelector((state) => state.marathons.marathonsById.response.data);
  const marathonLoading = useAppSelector((state) => state.marathons.marathonsById.response.loading);
  const registrationInfo = useAppSelector((state) => state.marathons.userRegistered.data);
  const isInsuranceNeeded = useAppSelector((state) => state.marathons.marathonsById.params.insurance);
  const products = useAppSelector((state) => state.products.productsById.params.products);
  const userId = useAppSelector((state) => state.users.userId);

  const distanceId = useAppSelector((state) => state.marathons.marathonsById.params.distance);

  const distancePeriodPrices = marathon?.distances?.find((distancePeriodPrice: any) => (
    Number(distancePeriodPrice?.id) === Number(distanceId)
  ))

  const registeredPeriodPrices = marathon?.distances?.find((distancePeriodPrice: any) => (
    Number(distancePeriodPrice?.id) === Number(registrationInfo?.distanceId)
  ))

  const periodPrice = (registrationInfo && !changeDistance) ? registeredPeriodPrices : distancePeriodPrices;

  const handleChange = (_: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const productsPayload = products?.map(product => ({
    productId: product.productId,
    quantity: product.quantity,
  }));

  const handleOpenChangeDistanceModal = () => {
    setIsChangeDistanceModalOpen(true);
  };

  const handleIsChangeDistanceModalClose = () => {
    setIsChangeDistanceModalOpen(false);
  };

  const tabs: TabItem[] = [
    {
      label: t("registerToMarathon.registration"),
      component:
        <RegistrationTab
          changeDistance={changeDistance}
          setChangeDistance={setChangeDistance}
          step={step}
          setStep={setStep}
          setEnteredPromocode={setEnteredPromocode}
          enteredPromocode={enteredPromocode}
          handleOpenChangeDistanceModal={handleOpenChangeDistanceModal}
        />
    },
    {
      label: t("registerToMarathon.information"),
      component: <InfoTab />
    },
  ];

  useEffect(() => {
    dispatch(getProductsByMarathonId({
      marathonId: Number(marathonId),
    }));
    dispatch(getMarathonUserRegistered
      ({
        marathonId: Number(marathonId)
      }));
    dispatch(getMarathonsHasUserInsurance({
      marathonId: Number(marathonId),
    }));
    dispatch(getMarathonAgreementsByMarathonId({
      marathonId: Number(marathonId),
    }))
  }, [dispatch, marathonId, i18n.language])

  useEffect(() => {
    dispatch(getUserMarathonProducts({
      marathonId: Number(marathonId),
      userId: Number(userId)
    }))
    dispatch(getMarathonsById({
      marathonId: Number(marathonId)
    }))
  }, [marathonId, i18n.language, dispatch])

  const handlePay = () => {
    const notificationClient = createSignalRConnection("/hubs/payment-notification");
    setPaymentConnection(notificationClient);

    const data = {
      distanceId: Number(distanceId),
      promocode: enteredPromocode || undefined,
      includeInsurance: isInsuranceNeeded,
      products: productsPayload,
      ...(enteredPromocode
        ? { promocode: enteredPromocode }
        : {}),
    }

    if (changeDistance === true) {
      dispatch(changeDistanceOfMarathon(data));
    } else {
      dispatch(registerUser(data));
    }
    handleIsChangeDistanceModalClose();
  }

  useEffect(() => {
    if (!paymentConnection) return;

    paymentConnection.onreconnected((connectionId) => {
      console.log(`Payment Hub переподключён, connectionId: ${connectionId}`);
    });

    paymentConnection.onclose((error) => {
      console.log(`Payment Hub закрыт: ${error ? error.message : "нет ошибки"}`);
    });

    paymentConnection.onreconnecting((error) => {
      console.log("Payment Hub пытается переподключиться", error);
    });

    paymentConnection.on("PaymentConfirmed", () => {
      localStorage.setItem("paymentSuccess", "true");
      window.location.reload();
    });
  }, [paymentConnection]);

  const showMobileSummary =
    isMobile && (!registrationInfo || changeDistance);

  if (marathonLoading || !marathon) {
    return (
      <Box
        sx={{
          height: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const isRegistrationInfoOpen = Boolean(registrationInfo && !changeDistance);

  return (
    <Box sx={{
      display: "flex",
      flexWrap: "wrap",
      flexDirection: { xs: "column", md: "row" },
      // alignItems: "flex-start",
      gap: isMobile ? "0" : "20px",
      margin: "0 auto",
      width: "80%",
      paddingTop: "32px",
      paddingBottom: { xs: "200px", md: "0" },

    }}>
      <Box
        sx={{
          // width: isMobile ? "100%" : "65%",
          width: {
            xs: "100%",
            md: "55%",
            lg: "65%",
          },
        }}
      >
        <Paper
          sx={{
            borderRadius: "16px",
            boxShadow: "none"
          }}
        >
          <Typography
            component="div"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: "24px",
              fontSize: "24px",
              fontWeight: 600,
              color: "#3E444A",
              marginBottom: "20px",
            }}
          >
            <ArrowBackIcon
              sx={{
                cursor: "pointer",
              }}
              onClick={() => navigate("/personal_cabinet/my_marathon")} />
            {marathon && marathon.name}
          </Typography>
        </Paper>

        <Tabs value={activeTab} onChange={handleChange} sx={{
          marginBottom: 3,
          maxWidth: isMobile ? "100%" : '70%',
          color: '#0090D1',
          '& .MuiTabs-indicator': {
            backgroundColor: '#0090D1',
          },
        }}>
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              sx={{
                color: '#3E444A',
                '&.Mui-selected': {
                  color: '#0090D1',
                  fontWeight: 600,
                },
              }}
            />
          ))}
        </Tabs>

        <Box>
          {tabs[activeTab].component}
        </Box>
      </Box>

      <Box sx={{
        width: {
          xs: "100%",
          md: "40%",
          lg: "30%",
        },
      }}>
        <Box>
          {showMobileSummary ? (
            <MobileSummery
              step={step}
              setStep={setStep}
              changeDistance={changeDistance}
              setChangeDistance={setChangeDistance}
              handleOpenChangeDistanceModal={handleOpenChangeDistanceModal}
            />
          ) : (
            <>
              {!isRegistrationInfoOpen && step !== 3 && (
                <Summary
                  showButton={true}
                  changeDistance={changeDistance}
                  setChangeDistance={setChangeDistance}
                />
              )}
            </>
          )}
        </Box>
        {activeTab === 0 && (distanceId || registrationInfo) && (
          <Box
            sx={{
              backgroundColor: "#fff",
              padding: "24px",
              borderRadius: "20px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              width: isMobile ? "100%" : "100%",
              height: "auto",
            }}
          >
            <Typography variant="h6" sx={{ fontSize: "24px", color: "#3E444A", marginBottom: "12px", fontWeight: "600" }}>
              {t("myMarathon.acceptancePeriod")}
            </Typography>

            {periodPrice?.distancePrices?.map((distancePrice: any) => (
              <Box
                key={distancePrice.id}
                sx={{
                  backgroundColor: distancePrice.isToday ? "#F1F5F9" : "#f5f5f5",
                  color: distancePrice.isToday ? "#0090D1" : "#333",
                  padding: "12px 16px",
                  borderRadius: "12px",
                  marginBottom: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body1" color="#3E444A">
                  {dayjs(distancePrice?.dateStart).format("DD.MM.YYYY")} -{" "}
                  {dayjs(distancePrice?.dateEnd).format("DD.MM.YYYY")}
                </Typography>
                <Typography variant="body2" fontWeight="500">
                  {distancePrice?.price} {t("myMarathon.som")}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <SecondConfirmModal
          open={isChangeDistanceModalOpen}
          title={changeDistance ? t("myMarathon.changeDistance") : t("myMarathon.marathonRegistration")}
          description={changeDistance ? t("myMarathon.areYouSureYouWantToChangeTheDistance") : t("myMarathon.areYouSureYouWantToRegisterForTheMarathon")}
          confirmText={changeDistance ? t("myMarathon.changeDistance") : t("header.register")}
          cancelText={t("buttons.cancel")}
          onConfirm={handlePay}
          onCancel={handleIsChangeDistanceModalClose}
        />
      </Box>
    </Box >
  );
};

export default RegistrationPage;
